import { getStatus, MedplumClient, OperationOutcomeError } from "@medplum/core";
import { Patient } from "@medplum/fhirtypes";

import {
  CLIENT_LOAD_FINAL_ISSUE_CODES,
  CLIENT_LOAD_FINAL_STATUSES,
  CLIENT_LOAD_RETRY_DELAYS_MS,
} from "@/clients/constants/client-chart";
import { getMedplumErrorIssueCodes, getMedplumErrorStatus } from "@/utils/medplum-http-error";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

/**
 * Searches for the client's Patient. A failed request is retried after each delay in
 * `CLIENT_LOAD_RETRY_DELAYS_MS`, and the last error is thrown once those run out. An error a retry
 * cannot change is thrown at once. The first failure that will be retried is reported to Sentry as a
 * warning, so failures the retries recover from stay visible.
 * @param medplum - Medplum client.
 * @param clientId - Patient id from the URL.
 * @param isStale - Returns true once the chart no longer needs this load, which stops retrying.
 * @returns The Patient, or undefined when no Patient has this id.
 */
export async function searchPatientWithRetry(
  medplum: MedplumClient,
  clientId: string,
  isStale: () => boolean,
): Promise<Patient | undefined> {
  for (let attempt = 0; ; attempt++) {
    try {
      // Retries skip the request cache, because the client keeps a failed search there.
      const searchOptions = attempt > 0 ? { cache: "reload" as const } : undefined;
      return await medplum.searchOne("Patient", { _id: clientId }, searchOptions);
    } catch (error) {
      if (attempt >= CLIENT_LOAD_RETRY_DELAYS_MS.length || isFinalClientLoadError(error))
        throw error;
      if (isStale()) throw error;
      if (attempt === 0) {
        captureExceptionWithoutPhi(
          "Client chart load failed, retrying",
          error,
          { clientId },
          "warning",
        );
      }
      await new Promise((resolve) => setTimeout(resolve, CLIENT_LOAD_RETRY_DELAYS_MS[attempt]));
      if (isStale()) throw error;
    }
  }
}

/**
 * Checks whether a failed Patient search is a server answer that a retry cannot change.
 * @param error - Error thrown by the search.
 * @returns True when the server answered with no access, no session, or no such Patient.
 */
export function isFinalClientLoadError(error: unknown): boolean {
  // An OperationOutcomeError has no status field. Its status comes from the outcome id.
  const status =
    getMedplumErrorStatus(error) ??
    (error instanceof OperationOutcomeError ? getStatus(error.outcome) : undefined);
  if (CLIENT_LOAD_FINAL_STATUSES.includes(status ?? 0)) return true;
  return getMedplumErrorIssueCodes(error).some((code) =>
    CLIENT_LOAD_FINAL_ISSUE_CODES.includes(code),
  );
}
